import type { RuntimeConfig } from '../types';
import { getApiBase, getPreviewToken } from '../utils/dom';

function configUrl(siteKey: string): string {
  const token = getPreviewToken();

  return token
    ? `${getApiBase()}/runtime/preview/${encodeURIComponent(token)}`
    : `${getApiBase()}/runtime/${encodeURIComponent(siteKey)}`;
}

export async function fetchConfig(siteKey: string): Promise<RuntimeConfig> {
  const response = await fetch(configUrl(siteKey), {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    cache: getPreviewToken() ? 'no-store' : 'default',
  });

  if (!response.ok) {
    throw new Error(`Orbit config unavailable (${response.status})`);
  }

  return (await response.json()) as RuntimeConfig;
}

/** Fire-and-forget heartbeat so the portal can confirm the snippet is live. */
export function sendHeartbeat(siteKey: string): void {
  if (getPreviewToken()) return;
  const url = `${getApiBase()}/runtime/${encodeURIComponent(siteKey)}/beacon`;
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, '{}');
    } else {
      void fetch(url, { method: 'POST', credentials: 'omit', keepalive: true, body: '{}' });
    }
  } catch {
    // best effort
  }
}

export function postConsentLog(
  siteKey: string,
  body: { visitor_key: string; action: string; categories?: Record<string, boolean> },
): void {
  const payload = JSON.stringify(body);
  const token = getPreviewToken();

  // Preview traffic goes to a sink endpoint so playground runs never skew reports.
  const url = token
    ? `${getApiBase()}/runtime/preview/${encodeURIComponent(token)}/consent`
    : `${getApiBase()}/runtime/${encodeURIComponent(siteKey)}/consent`;

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    return;
  }

  void fetch(url, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: payload,
    keepalive: true,
  });
}
